import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { TutorialProducts } from '~/dashboard/types/dashboard.types'
import { RootState } from '~/redux/store'
import ROUTES from '~/shared/constants/routes.const'

interface DashboardState {
  isTabToggled: boolean
  removedQuestIndex?: number
  highlightedItem: string | null
  isNavigateToLearningPath: boolean
  chestAnimation: boolean
  tutorial: {
    product: TutorialProducts
    tutorialStep: number
    shouldShowTutorial: boolean
    shouldShowLearningPathTutorial: boolean
  }
  openChecklist: boolean
  selectedGrade: number | string
  subjectOrder: number | string
  achievementPagePreviousUrl: string
  stepTwoExpanded: string[]
  streakUpdatedAt: number | null
}

const initialState: DashboardState = {
  isTabToggled: true,
  removedQuestIndex: undefined,
  highlightedItem: null,
  isNavigateToLearningPath: false,
  chestAnimation: false,
  tutorial: {
    product: TutorialProducts.EMPTY,
    tutorialStep: 0,
    shouldShowTutorial: false,
    shouldShowLearningPathTutorial: false,
  },
  openChecklist: false,
  selectedGrade: '',
  subjectOrder: '',
  achievementPagePreviousUrl: ROUTES.DASHBOARD,
  stepTwoExpanded: [],
  streakUpdatedAt: null,
}

const slice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    toggleTab(state, action: PayloadAction<boolean>) {
      state.isTabToggled = action.payload
    },
    setRemovedQuestIndex(state, action: PayloadAction<number | undefined>) {
      state.removedQuestIndex = action.payload
    },
    setHighlightedItem(state, action: PayloadAction<string | null>) {
      state.highlightedItem = action.payload
    },
    setNavigateFromLearningPath(state, action: PayloadAction<boolean>) {
      state.isNavigateToLearningPath = action.payload
    },
    setChecklistChestAnimation(state, action: PayloadAction<boolean>) {
      state.chestAnimation = action.payload
    },
    setTutorialStep(state, action: PayloadAction<number>) {
      state.tutorial.tutorialStep = action.payload
    },
    setShouldShowTutorial(state, action: PayloadAction<{ product: TutorialProducts; shouldShowTutorial: boolean }>) {
      state.tutorial = {
        ...state.tutorial,
        product: action.payload.product,
        shouldShowTutorial: action.payload.shouldShowTutorial,
      }
    },
    setOpenChecklist(state, action: PayloadAction<boolean>) {
      state.openChecklist = action.payload
    },
    setSelectedGrade(state, action: PayloadAction<number | string>) {
      state.selectedGrade = action.payload
    },
    setSelectedSubjectOrder: (state, action: PayloadAction<number>) => {
      state.subjectOrder = action.payload
    },
    setAchievementPagePreviousUrl: (state, action: PayloadAction<string>) => {
      state.achievementPagePreviousUrl = action.payload
    },
    setStepTwoExpanded: (state, action: PayloadAction<string[]>) => {
      state.stepTwoExpanded = action.payload
    },
    setStreakUpdatedAt: (state, action: PayloadAction<number>) => {
      state.streakUpdatedAt = action.payload
    },
  },
})

export const selectDashboardToggle = (state: RootState): boolean => {
  return state.dashboard.isTabToggled
}

export const selectRemovedQuestIndex = (state: RootState): number | undefined => {
  return state.dashboard.removedQuestIndex
}

export const selectHighlightedItem = (state: RootState): string | null => {
  return state.dashboard.highlightedItem
}

export const selectNavigateToLearningPath = (state: RootState): boolean => {
  return state.dashboard.isNavigateToLearningPath
}

export const selectChecklistChestAnimation = (state: RootState): boolean => {
  return state.dashboard.chestAnimation
}

export const selectTutorialStep = (state: RootState): number => {
  return state.dashboard.tutorial?.tutorialStep
}

export const selectShouldShowTutorial = (state: RootState): boolean => {
  return state.dashboard.tutorial?.shouldShowTutorial
}

export const selectOpenChecklist = (state: RootState): boolean => {
  return state.dashboard.openChecklist
}

export const selectTutorialProduct = (state: RootState): TutorialProducts => {
  return state.dashboard.tutorial?.product
}

export const selectSelectedGrade = (state: RootState): number | string => state.dashboard.selectedGrade
export const selectSubjectOrder = (state: RootState): number | string => state.dashboard.subjectOrder
export const selectAchievementPagePreviousUrl = (state: RootState): string => state.dashboard.achievementPagePreviousUrl
export const selectStepTwoExpanded = (state: RootState): string[] => state.dashboard.stepTwoExpanded
export const selectSreakUpdatedAt = (state: RootState): number | null => state.dashboard.streakUpdatedAt

export const {
  toggleTab,
  setRemovedQuestIndex,
  setHighlightedItem,
  setNavigateFromLearningPath,
  setChecklistChestAnimation,
  setTutorialStep,
  setShouldShowTutorial,
  setOpenChecklist,
  setSelectedGrade: setSelectedGradeActionCreator,
  setSelectedSubjectOrder: setSelectedSubjectOrderActionCreator,
  setAchievementPagePreviousUrl,
  setStepTwoExpanded,
  setStreakUpdatedAt,
} = slice.actions

export default slice.reducer
